/**
 * File: FilterSelect.tsx
 * Description: This file contains the code for the FilterSelect component
 * 
 */

// imports
import {InputLabel, Select, MenuItem, FormControl, FilledInput} from "@mui/material";
import { ListItemText } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';
import CheckBoxSharpIcon from '@mui/icons-material/CheckBoxSharp';

import './AppSelect.scss';
import React, { useState } from "react";
import {matDropDownStyle} from "../../assets/style/material/matSyles";
import { useEffect } from "react";

// interface for the props
interface AppSelectProps {
    selectLabel: string;
    fillElements: string[];
    onSelect?: any,
    selectRef?: any,
    selectedItem: any,
    multiSelect: boolean,
    name?: string;
    resetSelect?: any;
    msg?: string;
    errorMsg?: string;
    selectedStyle?: string;
    sort?:boolean;
    setSelectedParent?: any
    onChange?: (value: any) => void;
}

/**
 * The FilterSelect component is a Select input
 * @param props Props passed to the component
 *      - selectLabel {String} | Label for the select input
 *      - fillElements {String[]} | array of strings - the list of objects required in the select 
 *      - onSelect {() => {}} | An onSelect function that handles a selection event
 *      - selectedItem {String | String[]} | Selected items 
 *      - multiSelect {boolean} | can you select more than one value
 * @returns The FilterSelect JSX
 */
function AppSelect({selectLabel, fillElements, selectRef, selectedItem, multiSelect, name, resetSelect, msg, errorMsg, selectedStyle = "", sort=false, setSelectedParent, onChange} : AppSelectProps) {
    const [selected, setSelected] = useState(selectedItem);
    const dropDownClasses = matDropDownStyle();
    const [open, setOpen] = React.useState(false);

    //TODO: change to proper type
    const menuProps: any = {
        classes: {
            list: dropDownClasses.list
        },
        anchorOrigin: {
            vertical: "bottom",
            horizontal: "left"
        },
        transformOrigin: {
            vertical: "top",
            horizontal: "left"
        },
        getContentAnchorEl: null,
        variant: "menu"
    }


    // handles All for multiselect
    const handleChange = (e) => {
      let lastIndex = e.target.value[e.target.value.length-1]
      if (Array.isArray(e.target.value) && lastIndex === 'All') {
        return setSelected(['All']);
      }
      if (Array.isArray(e.target.value) &&
          e.target.value.length > 1 &&
          e.target.value.includes('All')) {
        return setSelected(e.target.value.filter(f => f !== 'All'));
      }
      if(setSelectedParent) {
          multiSelect
          ? setSelectedParent(e.target.value.sort((a,b) => a < b ? -1 : a > b ? 1 : 0))
          : setSelectedParent(e.target.value);
      } 

      const newValue = multiSelect
        ? e.target.value.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
        : e.target.value;

      setSelected(newValue);

      if (typeof onChange === 'function') {
        onChange(newValue);
      }

    };

    const handleReset = () => {
      setSelected(selectedItem)
    }

    useEffect(() => {
      handleReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[resetSelect])


    const handleOpenClose = () => {
        setOpen(!open);
    };

    const iconComponent = () => {
        return (
            <div onClick={handleOpenClose} className="icon_container">
                <i className="icon-chevron-thin-down"   />
            </div>
        )}

    /** onSelectMulti
     * This triggers only if multiSelect is true - adds/removes the item from multiselect and passes it to the onSelect function in props
     * @param event the select event trigger
     */


    const selectClass = `${dropDownClasses.select} ${selectedStyle}`;
    // to sort when sort prop is passed
    if(sort){
        fillElements = sort ? fillElements.slice().sort((a,b) => a > b ? 1 : b > a ? -1 : 0): fillElements;    
        let indexOfAll = fillElements?.indexOf('All');
        let all = fillElements[indexOfAll];   
        fillElements.splice(indexOfAll,1);
        fillElements.unshift(all);
    }
    return(
        <FormControl className="select-dropdown">
            <InputLabel id='inputLabel' className={dropDownClasses.label}>{selectLabel}</InputLabel>
            <Select
                className={selectClass}
                MenuProps={menuProps}
                labelId= "material-select-element"
                input={<FilledInput />}
                id={selectLabel}
                name={name ? name : selectLabel}
                label={selectLabel}
                value={selected}
                onChange={e => handleChange(e)}
                open={open}
                onClose={handleOpenClose}
                onOpen={handleOpenClose}
                ref={el => selectRef ? selectRef.current[name ? name : selectLabel] = el : ''}
                IconComponent={iconComponent}
                multiple={multiSelect}
                disableUnderline
                renderValue={ multiSelect ? ((selected) => (selected as string[]).join(', ')) : (undefined) }
                >
                    {/* This will loop through the list of elements to add them to the select if the element list is not null or empty */}
                   {fillElements ? fillElements.map((element, i) => {
                        // if it is not a multiselect
                        if(!multiSelect)
                        {
                            return <MenuItem value={element} key={`${element}-${i}`}>{element}</MenuItem>
                        }
                        // if the Select is a multiselect then return a checkbox select Item 
                        return (
                            <MenuItem key={`${multiSelect? 'multi' : ''}${element}-${i}`} value={element}>
                                <Checkbox
                                    className='styledCheckBox'
                                    icon={<CheckBoxOutlineBlankSharpIcon className='check-box-icon-outline' />}
                                    checkedIcon={<CheckBoxSharpIcon className='check-box-con-solid' />}
                                    checked={selected.indexOf(element) > -1}
                                />
                                <ListItemText primary={element} />
                            </MenuItem>
                        )
                    }) : null}
                </Select>
                {/*TODO: these need to be refactored out, shouldn't be part of this component*/}
                {msg ? <p><span className="icon-Confirmation-circle-green"></span><span  dangerouslySetInnerHTML={{ __html: msg }}></span></p>: ""}
                {errorMsg ? <p className="error"><span className="icon-Error-warning"></span><span  dangerouslySetInnerHTML={{ __html: errorMsg }}></span></p>: ""}
        </FormControl>
    );
}

export default AppSelect;
